import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Box,
  Stack,
  CircularProgress,
  Breadcrumbs,
  Link,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip
} from "@mui/material";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import { notification } from "src/utils/messages";
import { UpdateMemberPost } from "src/api/commonapi";
import { getChapterPostStructure, getChapterMembers, removeCoordinator } from "src/api/coordinatorApi";
import MemberPickerModal from "src/components/MemberPickerModal";
import CoordinatorSection from "src/components/CoordinatorSection";
import CascadeConfirmModal from "src/components/CascadeConfirmModal";
import RoleFormDrawer from "src/components/RoleFormDrawer";
import Page from "src/components/Page";
import { Routes as ApiRoutes } from "src/constant/api_url";

export default function AllocatePostPage() {
  const { chapterId } = useParams();
  const navigate = useNavigate();

  const [structure, setStructure] = useState([]);
  const [chapterName, setChapterName] = useState("");
  const [memberList, setMemberList] = useState([]);
  const [selectedHolders, setSelectedHolders] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [cascadeModalOpen, setCascadeModalOpen] = useState(false);
  const [pendingChangedPostTypes, setPendingChangedPostTypes] = useState([]);
  const [removedPostTypeIds, setRemovedPostTypeIds] = useState([]);
  const [coordinatorCounts, setCoordinatorCounts] = useState({});
  const [roleFormOpen, setRoleFormOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [holderPickerOpen, setHolderPickerOpen] = useState(false);
  const [holderPickerPostType, setHolderPickerPostType] = useState(null);
  const [coordToHolderModal, setCoordToHolderModal] = useState({ open: false, postTypeId: null, memberId: null, memberName: "", coordinatorIds: [] });
  const [stagedCoordRemovals, setStagedCoordRemovals] = useState([]);

  // Single primitive for all holder staging: sets the staged holder and
  // replaces that post's staged coordinator removals (nothing is called yet —
  // removals + allocation all fire together on Update All Posts).
  const stageHolderChange = (postTypeId, memberId, removalIds = []) => {
    setSelectedHolders((prev) => ({ ...prev, [postTypeId]: memberId }));
    setStagedCoordRemovals((prev) => [
      ...prev.filter((r) => r.postTypeId !== postTypeId),
      ...removalIds.map((id) => ({ id, postTypeId, memberId }))
    ]);
  };
  const [removeHolderModal, setRemoveHolderModal] = useState({ open: false, postTypeId: null, memberName: "", postName: "" });
  const [ltMoveModal, setLtMoveModal] = useState({ open: false, fromPostTypeId: null, fromPostName: "", toPostTypeId: null, toPostName: "", memberId: null, memberName: "" });
  const [moveLinks, setMoveLinks] = useState({});

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [structureResp, membersResp] = await Promise.all([
        getChapterPostStructure(chapterId),
        getChapterMembers({ chpt_id: parseInt(chapterId, 10), page: 1, limit: 100 })
      ]);

      if (structureResp?.status === 1) {
        setStructure(structureResp.output || []);
        setChapterName(structureResp.chpt_name || "");
        const holders = {};
        const counts = {};
        (structureResp.output || []).forEach((pt) => {
          holders[pt.id] = pt.host?.m_id || 0;
          let totalCoords = 0;
          (pt.roles || []).forEach((r) => {
            totalCoords += (r.member_list || []).length;
          });
          counts[pt.id] = totalCoords;
        });
        setSelectedHolders(holders);
        setCoordinatorCounts(counts);
      }

      if (membersResp?.status === 1) {
        setMemberList(membersResp.output?.data || []);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [chapterId]);

  useEffect(() => {
    if (chapterId) fetchData();
  }, [chapterId, fetchData]);

  const handleHolderChange = (postTypeId, mId) => {
    if (mId === 0) {
      stageHolderChange(postTypeId, 0);
      return;
    }

    const coordinatorIds = getCoordinatorAssignmentIds(postTypeId, mId);
    const member = memberList.find((m) => m.m_id === mId);
    if (coordinatorIds.length > 0) {
      setCoordToHolderModal({
        open: true,
        postTypeId,
        memberId: mId,
        memberName: member?.m_name || "",
        coordinatorIds
      });
      return;
    }

    stageHolderChange(postTypeId, mId);
  };

  const handleHolderPickerOpen = (pt) => {
    setHolderPickerPostType(pt);
    setHolderPickerOpen(true);
  };

  const handleHolderPickerClose = () => {
    setHolderPickerOpen(false);
    setHolderPickerPostType(null);
  };

  const handleHolderPickerConfirm = (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) return;
    const mId = selectedIds[0];
    const targetPt = holderPickerPostType;
    handleHolderPickerClose();
    if (!targetPt) return;
    if ((selectedHolders[targetPt.id] ?? targetPt.host?.m_id ?? 0) === mId) return; // no-op re-pick
    // LT-to-LT move: member already (effectively) holds another post in this chapter
    const fromPt = structure.find((pt) => pt.id !== targetPt.id && effectiveHolderOf(pt) === mId);
    if (fromPt && mId !== 0) {
      const member = memberList.find((m) => m.m_id === mId);
      setLtMoveModal({
        open: true,
        fromPostTypeId: fromPt.id,
        fromPostName: fromPt.name || "",
        toPostTypeId: targetPt.id,
        toPostName: targetPt.name || "",
        memberId: mId,
        memberName: member?.m_name || "Selected member"
      });
      return;
    }
    // Manual pick overrides any previous move link on this post
    setMoveLinks((prev) => {
      if (!prev[targetPt.id] || prev[targetPt.id].member === mId) return prev;
      const next = { ...prev };
      delete next[targetPt.id];
      return next;
    });
    handleHolderChange(targetPt.id, mId);
  };

  const effectiveHolderOf = (pt) => {
    const original = pt.host?.m_id || 0;
    const staged = selectedHolders[pt.id];
    if (staged === undefined || staged === original) return original;
    return staged;
  };

  const handleLtMoveCancel = () => {
    setLtMoveModal({ open: false, fromPostTypeId: null, fromPostName: "", toPostTypeId: null, toPostName: "", memberId: null, memberName: "" });
  };

  const handleLtMoveConfirm = () => {
    const { fromPostTypeId, toPostTypeId, memberId } = ltMoveModal;
    // Stage both sides only — old post vacated, new post assigned. If the member
    // also coordinates under the new post, those rows are staged too. Saved later.
    stageHolderChange(toPostTypeId, memberId, getCoordinatorAssignmentIds(toPostTypeId, memberId));
    stageHolderChange(fromPostTypeId, 0);
    setMoveLinks((prev) => {
      const next = { ...prev };
      delete next[toPostTypeId];
      Object.keys(next).forEach((k) => { if (next[k].from === toPostTypeId) delete next[k]; });
      next[toPostTypeId] = { from: fromPostTypeId, member: memberId };
      return next;
    });
    handleLtMoveCancel();
  };

  const handleHolderRevert = (pt) => {
    const original = pt.host?.m_id || 0;
    const updates = { [pt.id]: original };
    const link = moveLinks[pt.id];
    if (link && (selectedHolders[pt.id] ?? original) === link.member) {
      // Reverting the TO side undoes the whole move
      const fromPt = structure.find((p) => p.id === link.from);
      if (fromPt) updates[link.from] = fromPt.host?.m_id || 0;
    } else if (!link) {
      // Reverting the FROM side undoes the move only if TO still stages the moved member
      const toEntry = Object.entries(moveLinks).find(([, l]) => l.from === pt.id);
      if (toEntry) {
        const toId = Number(toEntry[0]);
        const toPt = structure.find((p) => p.id === toId);
        if (toPt && (selectedHolders[toId] ?? toPt.host?.m_id ?? 0) === toEntry[1].member) {
          updates[toId] = toPt.host?.m_id || 0;
        }
      }
    }
    setSelectedHolders((prev) => ({ ...prev, ...updates }));
    // Undoing a holder staging also drops that post's staged coordinator removals
    setStagedCoordRemovals((prev) => prev.filter((r) => !(r.postTypeId in updates)));
    setMoveLinks((prev) => {
      const next = { ...prev };
      delete next[pt.id];
      Object.keys(next).forEach((k) => { if (next[k].from === pt.id) delete next[k]; });
      return next;
    });
  };

  const handleCoordToHolderConfirm = () => {
    const { postTypeId, memberId, coordinatorIds } = coordToHolderModal;
    // Stage only — holder assignment + coordinator removals apply together on save.
    setMoveLinks((prev) => {
      if (!prev[postTypeId] || prev[postTypeId].member === memberId) return prev;
      const next = { ...prev };
      delete next[postTypeId];
      return next;
    });
    stageHolderChange(postTypeId, memberId, coordinatorIds);
    setCoordToHolderModal({ open: false, postTypeId: null, memberId: null, memberName: "", coordinatorIds: [] });
  };

  const handleCoordToHolderCancel = () => {
    setCoordToHolderModal({ open: false, postTypeId: null, memberId: null, memberName: "", coordinatorIds: [] });
  };

  const handleHolderRemoveClick = (pt) => {
    setRemoveHolderModal({
      open: true,
      postTypeId: pt.id,
      memberName: pt.host?.m_name || "",
      postName: pt.name || ""
    });
  };

  const handleHolderRemoveCancel = () => {
    setRemoveHolderModal({ open: false, postTypeId: null, memberName: "", postName: "" });
  };

  const handleHolderRemoveConfirm = () => {
    const { postTypeId } = removeHolderModal;
    stageHolderChange(postTypeId, 0);
    handleHolderRemoveCancel();
  };

  const getCoordinatorAssignmentIds = (postTypeId, memberId) => {
    const pt = structure.find((p) => p.id === postTypeId);
    const ids = [];
    (pt?.roles || []).forEach((r) => {
      (r.member_list || []).forEach((c) => {
        if (c.m_id === memberId) ids.push(c.id);
      });
    });
    return ids;
  };

  const getPhotoUrl = (photo) => {
    if (photo) return `${ApiRoutes.API_HOSTNAME}images/member/${photo}`;
    return null;
  };

  const isHolderPending = (pt) => (selectedHolders[pt.id] || 0) !== (pt.host?.m_id || 0);

  const buildAllocations = (cascadeMap = {}) => {
    return structure
      .filter((pt) => {
        const currentHolder = selectedHolders[pt.id] || 0;
        const originalHolder = pt.host?.m_id || 0;
        if (currentHolder === originalHolder) return false;
        // Vacated posts are omitted (absent = vacated, coordinators stay) —
        // unless the wipe toggle is on, in which case they travel like a
        // change entry with member_id 0 + remove_coordinators true.
        if (currentHolder === 0) return !!cascadeMap[pt.id];
        return true;
      })
      .map((pt) => {
        const memberId = selectedHolders[pt.id] || 0;
        return {
          post_type_id: pt.id,
          // No member_id on vacated posts: 0 is not a valid member and the
          // backend rejects it. Omission + remove_coordinators carries the
          // wipe instruction (flat field stays omitted = vacated).
          ...(memberId !== 0 && { member_id: memberId }),
          remove_coordinators: !!cascadeMap[pt.id]
        };
      });
  };

  const handleSaveAll = async () => {
    const changedTypes = structure.filter((pt) => {
      const current = selectedHolders[pt.id] || 0;
      const original = pt.host?.m_id || 0;
      return current !== original;
    });

    const needsCascade = changedTypes.filter((pt) => (coordinatorCounts[pt.id] || 0) > 0);

    if (needsCascade.length > 0) {
      setPendingChangedPostTypes(needsCascade);
      setRemovedPostTypeIds(
        needsCascade.filter((pt) => !(selectedHolders[pt.id] || 0)).map((pt) => pt.id)
      );
      setCascadeModalOpen(true);
      return;
    }

    await executeSave({});
  };

  // Self-role assignments anchored under a changed/vacated post are removed by
  // the backend automatically. Count them (deduped by row id) so the
  // coordinators_removed assertion only fires for genuinely unexpected wipes.
  const countSelfAutoRemoved = () => {
    const affected = new Set();
    structure.forEach((pt) => {
      const current = selectedHolders[pt.id] || 0;
      const original = pt.host?.m_id || 0;
      if (current !== original) affected.add(pt.id);
    });
    const ids = new Set();
    structure.forEach((pt) => {
      (pt.roles || []).forEach((r) => {
        if (r?.is_self !== 1) return;
        (r.member_list || []).forEach((c) => {
          const anchor = c.assigned_under_post_type_id ?? pt.id;
          if (affected.has(anchor)) ids.add(c.id);
        });
      });
    });
    return ids.size;
  };

  const executeSave = async (cascadeMap) => {
    setIsSaving(true);
    try {
      // 1. Apply staged coordinator removals first — the new holder must not
      // still hold coordinator rows when the allocation lands. Any failure
      // aborts the whole save with staging intact for retry.
      const pendingRemovals = [...stagedCoordRemovals];
      if (pendingRemovals.length > 0) {
        const failedIds = [];
        for (const r of pendingRemovals) {
          try {
            const resp = await removeCoordinator({ id: r.id });
            if (resp?.status !== 1) failedIds.push(r.id);
          } catch (error) {
            failedIds.push(r.id);
          }
        }
        const failedSet = new Set(failedIds);
        setStagedCoordRemovals((prev) => prev.filter((x) => failedSet.has(x.id)));
        if (failedIds.length > 0) {
          notification({ type: "error", message: `Could not remove ${failedIds.length} staged coordinator(s). Update cancelled — please retry.` })();
          return;
        }
      }

      const allocations = buildAllocations(cascadeMap);
      // Omitted = vacated: only send posts that have a holder. Unchanged holders
      // are re-sent; removed/vacant posts are left out (never send 0).
      const payload = {
        chpt_id: parseInt(chapterId, 10),
        ...(selectedHolders[1] ? { pre_id: selectedHolders[1] } : {}),
        ...(selectedHolders[2] ? { vpre_id: selectedHolders[2] } : {}),
        ...(selectedHolders[3] ? { st_id: selectedHolders[3] } : {}),
        ...(selectedHolders[4] ? { lvh_id: selectedHolders[4] } : {}),
        ...(allocations.length > 0 ? { allocations } : {})
      };

      const resp = await UpdateMemberPost(payload);
      notification({
        type: resp.status === 0 ? "error" : "success",
        message: resp.message
      })();

      if (resp.status === 1) {
        const wiped = resp.output?.coordinators_removed || 0;
        const wipeRequested = Object.values(cascadeMap || {}).some(Boolean);
        const selfAutoRemoved = countSelfAutoRemoved();
        if (wiped > 0 && !wipeRequested && wiped > selfAutoRemoved) {
          notification({
            type: "warn",
            message: `Warning: ${wiped - selfAutoRemoved} coordinator(s) were removed by this update though removal was not requested.`
          })();
        }
        setStagedCoordRemovals([]);
        fetchData();
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to update posts" })();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCascadeConfirm = async (cascadeMap) => {
    setCascadeModalOpen(false);
    setPendingChangedPostTypes([]);
    setRemovedPostTypeIds([]);
    await executeSave(cascadeMap);
  };

  const handleCascadeCancel = () => {
    setCascadeModalOpen(false);
    setPendingChangedPostTypes([]);
    setRemovedPostTypeIds([]);
  };

  const handleEditRole = (roleDetail) => {
    setRoleToEdit(roleDetail);
    setRoleFormOpen(true);
  };

  const handleRoleFormSuccess = () => {
    setRoleFormOpen(false);
    fetchData();
  };

  const postTypeOptions = (structure || []).map((pt) => ({ id: pt.id, name: pt.name, is_self: pt.is_self }));

  const renderHolderCard = (pt) => {
    const host = pt.host;
    const pending = isHolderPending(pt);
    const removalPending = pending && (selectedHolders[pt.id] || 0) === 0;
    const stagedId = selectedHolders[pt.id] || 0;
    const isChange = pending && !removalPending;
    const stagedMember = isChange ? memberList.find((m) => m.m_id === stagedId) : null;
    return (
      <Box
        key={pt.id}
        sx={{
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: pending ? "primary.main" : "divider",
          bgcolor: "background.paper",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: "1 0 250px",
          minWidth: 250,
          transition: "border-color 0.15s ease"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, opacity: pending ? 0.65 : 1 }}>
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Avatar
              src={getPhotoUrl(host?.photo)}
              sx={{ width: 48, height: 48, border: "2px solid", borderColor: "primary.light" }}
            >
              {(host?.m_name || "?").charAt(0).toUpperCase()}
            </Avatar>
            {host?.m_verify_status === 2 && (
              <Box
                component="img"
                src="/static/icons/verified_blue.png"
                alt="Verified"
                sx={{
                  width: 20,
                  height: 20,
                  position: "absolute",
                  top: -2,
                  right: 2
                }}
              />
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              title={host?.m_name || "Not Assigned"}
              sx={{
                fontWeight: 600,
                color: "text.primary",
                overflowWrap: "anywhere",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.25
              }}
            >
              {host?.m_name || "Not Assigned"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {host?.business_name || pt.category}
            </Typography>
          </Box>
        </Box>

        {isChange && (
          <Box sx={{ borderTop: "1px dashed", borderColor: "divider", pt: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
              Will update to:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                src={stagedMember ? getPhotoUrl(stagedMember.photo) : null}
                sx={{ width: 32, height: 32, flexShrink: 0 }}
              >
                {(stagedMember?.m_name || "?").charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  noWrap
                  title={stagedMember?.m_name || "Selected member"}
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {stagedMember?.m_name || "Selected member"}
                </Typography>
                {stagedMember?.business_name && (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    {stagedMember.business_name}
                  </Typography>
                )}
              </Box>
              <Chip
                label="New"
                size="small"
                color="success"
                sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, flexShrink: 0 }}
              />
            </Box>
          </Box>
        )}

        {removalPending && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: "warning.lighter"
            }}
          >
            <WarningIcon sx={{ fontSize: 16, color: "warning.dark", flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 600 }}>
              This profile will be removed.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            pt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          {pending ? (
            <>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.3 }}>
                  {pt.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 600, display: "block", lineHeight: 1.3 }}>
                  {removalPending ? "Removal pending" : "Change pending"}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => handleHolderRevert(pt)}
                sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, flexShrink: 0 }}
              >
                Revert
              </Button>
            </>
          ) : (
            <>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {pt.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {host?.m_id ? (
                  <Tooltip title="Remove from post">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleHolderRemoveClick(pt)}
                      sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, minWidth: 0, px: 1 }}
                    >
                      Remove
                    </Button>
                  </Tooltip>
                ) : null}
                <Button
                  size="small"
                  color="primary"
                  startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                  onClick={() => handleHolderPickerOpen(pt)}
                  sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600 }}
                >
                  Change
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Page title="Allocate Post | LVB Admin">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  return (
    <Page title="Allocate Post | LVB Admin">
      <Container maxWidth="xl">
        <Breadcrumbs separator="›" sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate("/chapter")}
            sx={{ color: "text.secondary", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            Chapter
          </Link>
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
            Allocate Posts
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Allocate Post - Coordinators
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {chapterName ? `Chapter: ${chapterName}` : ""} — Assign and manage coordinators across leadership roles.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Update All Posts"}
          </Button>
        </Stack>

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
              mb: 1.5,
              display: "block"
            }}
          >
            Primary Post Holders
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "grey.300", borderRadius: 3 }
            }}
          >
            {structure.map((pt) => renderHolderCard(pt))}
          </Box>
        </Box>

        {structure.map((pt) => (
          <Box key={pt.id} sx={{ mb: 4, position: "relative", zIndex: 1 }}>
            <CoordinatorSection
              chapterId={chapterId}
              postType={pt}
              postTypeName={pt.name}
              stagedRemovedIds={stagedCoordRemovals.map((r) => r.id)}
              onRefresh={fetchData}
              onEditRole={handleEditRole}
            />
          </Box>
        ))}

        <MemberPickerModal
          open={holderPickerOpen}
          onClose={handleHolderPickerClose}
          onConfirm={handleHolderPickerConfirm}
          chapterId={chapterId}
          currentHolderId={holderPickerPostType?.host?.m_id || null}
          single
          title="Change Post Holder"
          subtitle={`Select a member to assign as ${holderPickerPostType?.name || "post holder"}.`}
          confirmLabel="Assign"
        />

        <Dialog
          open={removeHolderModal.open}
          onClose={handleHolderRemoveCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem", pb: 1 }}>
            Remove from Post?
          </DialogTitle>
          <DialogContent sx={{ pt: "8px !important" }}>
            <Typography variant="body2" color="text.secondary">
              Remove <strong>{removeHolderModal.memberName}</strong> from <strong>{removeHolderModal.postName}</strong>?
              {(coordinatorCounts[removeHolderModal.postTypeId] || 0) > 0 && (
                <> Their coordinators will stay and appear under the next {removeHolderModal.postName}.</>
              )}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={handleHolderRemoveCancel}
              variant="outlined"
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleHolderRemoveConfirm}
              variant="contained"
              color="error"
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={ltMoveModal.open}
          onClose={handleLtMoveCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem", pb: 1 }}>
            Move Post Holder?
          </DialogTitle>
          <DialogContent sx={{ pt: "8px !important" }}>
            <Typography variant="body2" color="text.secondary">
              Assigning <strong>{ltMoveModal.memberName}</strong> as <strong>{ltMoveModal.toPostName}</strong> will
              remove them from <strong>{ltMoveModal.fromPostName}</strong> and allocate them to the new post.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Both changes stay staged (Removal pending / Change pending) until you click Update All Posts.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={handleLtMoveCancel}
              variant="outlined"
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLtMoveConfirm}
              variant="contained"
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              Move
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={coordToHolderModal.open}
          onClose={handleCoordToHolderCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem", pb: 1 }}>
            Confirm Coordinator Removal
          </DialogTitle>
          <DialogContent sx={{ pt: "8px !important" }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{coordToHolderModal.memberName}</strong> is currently a <strong>Chapter coordinator</strong> for this post.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Assigning them as post holder will <strong>remove them</strong> from all coordinator roles under this post.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              The removal will be staged and applied together with the holder change when you click Update All Posts.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontWeight: 500 }}>
              Do you want to continue?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={handleCoordToHolderCancel}
              variant="outlined"
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCoordToHolderConfirm}
              variant="contained"
              color="error"
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <CascadeConfirmModal
          open={cascadeModalOpen}
          onClose={handleCascadeCancel}
          onConfirm={handleCascadeConfirm}
          pendingChangedPostTypes={pendingChangedPostTypes}
          removedPostTypeIds={removedPostTypeIds}
          coordinatorCounts={coordinatorCounts}
          loading={isSaving}
        />

        <RoleFormDrawer
          open={roleFormOpen}
          onClose={() => setRoleFormOpen(false)}
          onSuccess={handleRoleFormSuccess}
          editMode
          roleData={roleToEdit}
          postTypes={postTypeOptions}
        />
      </Container>
    </Page>
  );
}
